// Plankkä wishlist — persistent localStorage wishlist with header badge,
// cross-tab sync, and client-side page rendering.
// ASCII-only quotes; no libraries; ES2022+; ~2.5 KB.

var KEY = 'plankka:wishlist';

function read() {
  try {
    return JSON.parse(localStorage.getItem(KEY)) || [];
  } catch (e) {
    return [];
  }
}

function write(arr) {
  localStorage.setItem(KEY, JSON.stringify(arr));
  // Fire same-tab event because storage event only fires in OTHER tabs.
  window.dispatchEvent(new CustomEvent('plankka:wishlist:change'));
}

function has(handle) {
  return read().includes(handle);
}

function toggle(handle) {
  var list = read();
  var idx = list.indexOf(handle);
  if (idx === -1) {
    list.push(handle);
  } else {
    list.splice(idx, 1);
  }
  write(list);
  return list.includes(handle);
}

class PlankkaWishlist extends HTMLElement {
  connectedCallback() {
    this.ac = new AbortController();
    var sig = { signal: this.ac.signal };

    // Only the first instance owns the document-delegated click handler.
    if (!PlankkaWishlist.bound) {
      PlankkaWishlist.bound = true;
      this._ownsDocHandler = true;
      document.addEventListener('click', function(e) {
        var btn = e.target.closest('.product-card__wishlist, .pdp-buy-panel__wishlist');
        if (!btn) return;
        e.preventDefault();
        var handle = btn.dataset.productHandle;
        if (!handle) return;
        toggle(handle);
        // syncAll is triggered via the plankka:wishlist:change event fired by write().
      }, sig);
    }

    window.addEventListener('storage', function(e) {
      if (e.key === KEY || e.key === null) {
        this.syncAll();
      }
    }.bind(this), sig);

    window.addEventListener('plankka:wishlist:change', function() {
      this.syncAll();
    }.bind(this), sig);

    this.syncAll();

    // Page-render mode: activated when the element has a [data-wishlist-results]
    // descendant (or the 'page' attribute).
    if (this.hasAttribute('page') || this.querySelector('[data-wishlist-results]')) {
      this.renderPage();
    }
  }

  disconnectedCallback() {
    this.ac.abort();
    if (this._ownsDocHandler) {
      PlankkaWishlist.bound = false;
    }
  }

  syncAll() {
    var list = read();
    // Reflect every heart button on the page.
    document.querySelectorAll('.product-card__wishlist, .pdp-buy-panel__wishlist').forEach(function(btn) {
      var handle = btn.dataset.productHandle;
      if (!handle) return;
      var active = list.includes(handle);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      btn.classList.toggle('product-card__wishlist--active', active);
      btn.classList.toggle('is-active', active);
    });

    // Page mode: drop cards whose handle was removed; restore empty state at zero.
    var resultsEl = this.querySelector('[data-wishlist-results]');
    if (resultsEl && !resultsEl.hidden) {
      resultsEl.querySelectorAll('.product-card__wishlist[data-product-handle]').forEach(function(btn) {
        if (!list.includes(btn.dataset.productHandle)) {
          var card = btn.closest('.product-card');
          if (card) card.remove();
        }
      });
      if (resultsEl.children.length === 0) {
        resultsEl.hidden = true;
        var emptyEl = this.querySelector('[data-wishlist-empty]');
        if (emptyEl) emptyEl.hidden = false;
      }
    }

    // Update header badge.
    var host = document.querySelector('.site-header__wishlist-count-host');
    if (!host) return;
    var count = list.length;
    // Clear existing badge first (safe: host is a stable server-rendered wrapper).
    while (host.firstChild) host.removeChild(host.firstChild);
    if (count > 0) {
      var badge = document.createElement('span');
      badge.className = 'site-header__cart-count';
      badge.setAttribute('aria-hidden', 'true');
      badge.textContent = String(count);
      host.appendChild(badge);
    }
  }

  renderPage() {
    var resultsEl = this.querySelector('[data-wishlist-results]');
    var emptyEl = this.querySelector('[data-wishlist-empty]');
    var loadingEl = this.querySelector('[data-wishlist-loading]');

    if (!resultsEl) return;

    var handles = read();

    if (handles.length === 0) {
      if (emptyEl) emptyEl.hidden = false;
      if (loadingEl) loadingEl.hidden = true;
      resultsEl.hidden = true;
      return;
    }

    if (loadingEl) loadingEl.hidden = false;
    if (emptyEl) emptyEl.hidden = true;
    resultsEl.hidden = true;

    var pruned = [];
    var cards = [];
    var total = handles.length;
    var done = 0;
    var concurrency = 4;
    var idx = 0;

    var self = this;

    function finish() {
      done++;
      if (done < total) return;
      // All fetches settled.
      if (pruned.length > 0) {
        var remaining = read().filter(function(h) { return !pruned.includes(h); });
        write(remaining);
        self.syncAll();
      }
      if (loadingEl) loadingEl.hidden = true;
      if (cards.length === 0) {
        if (emptyEl) emptyEl.hidden = false;
        resultsEl.hidden = true;
      } else {
        // Append in original order by slot index.
        cards.sort(function(a, b) { return a.slot - b.slot; });
        cards.forEach(function(c) { resultsEl.appendChild(c.node); });
        resultsEl.hidden = false;
      }
    }

    function fetchNext() {
      if (idx >= handles.length) return;
      var slot = idx;
      var handle = handles[idx];
      idx++;

      fetch('/products/' + handle + '.js')
        .then(function(r) {
          if (!r.ok) {
            if (r.status === 404) pruned.push(handle);
            finish();
          } else {
            return r.json().then(function(p) {
              var node = buildCard(p);
              cards.push({ slot: slot, node: node });
              finish();
              fetchNext();
            });
          }
        })
        .catch(function() {
          finish();
        });
    }

    // Start up to `concurrency` workers.
    var workers = Math.min(concurrency, handles.length);
    for (var i = 0; i < workers; i++) {
      fetchNext();
    }
  }
}

PlankkaWishlist.bound = false;

function buildCard(p) {
  var card = document.createElement('div');
  card.className = 'product-card';

  var photo = document.createElement('div');
  photo.className = 'product-card__photo';

  // Stretched link
  var link = document.createElement('a');
  link.className = 'product-card__link';
  link.href = p.url || '/products/' + p.handle;
  link.setAttribute('aria-label', p.title || '');
  photo.appendChild(link);

  // Heart button (already in wishlist)
  var heart = document.createElement('button');
  heart.type = 'button';
  heart.tabIndex = -1;
  heart.className = 'product-card__wishlist product-card__wishlist--active';
  heart.setAttribute('aria-pressed', 'true');
  heart.setAttribute('aria-label', 'Verlanglijst');
  heart.dataset.productHandle = p.handle;
  // Build heart SVG via DOM — no innerHTML, no user data.
  var ns = 'http://www.w3.org/2000/svg';
  var svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('width', '18');
  svg.setAttribute('height', '18');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'currentColor');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.8');
  svg.setAttribute('aria-hidden', 'true');
  var path = document.createElementNS(ns, 'path');
  path.setAttribute('d', 'M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z');
  svg.appendChild(path);
  heart.appendChild(svg);
  photo.appendChild(heart);

  // Image
  if (p.featured_image) {
    var img = document.createElement('img');
    var src = p.featured_image;
    // Ensure protocol
    if (src.startsWith('//')) src = 'https:' + src;
    img.src = src;
    img.alt = p.title || '';
    img.loading = 'lazy';
    img.decoding = 'async';
    photo.appendChild(img);
  }

  card.appendChild(photo);

  // Info
  var info = document.createElement('div');
  info.className = 'product-card__info';

  var nameEl = document.createElement('span');
  nameEl.className = 'product-card__name';
  nameEl.textContent = p.title || '';
  info.appendChild(nameEl);

  // Price
  var priceRow = document.createElement('div');
  priceRow.className = 'product-card__price-row';
  var priceEl = document.createElement('span');
  priceEl.className = 'product-card__price';
  // Format cents to euros with Dutch comma separator.
  priceEl.textContent = formatMoney(p.price);
  priceRow.appendChild(priceEl);
  info.appendChild(priceRow);

  card.appendChild(info);
  return card;
}

function formatMoney(cents) {
  if (!cents && cents !== 0) return '';
  var fixed = (cents / 100).toFixed(2);
  var parts = fixed.split('.');
  var intStr = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return '€' + intStr + ',' + parts[1];
}

if (!customElements.get('plankka-wishlist')) {
  customElements.define('plankka-wishlist', PlankkaWishlist);
}
